import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import './Wallet.css';

function Wallet() {
  const [topUpAmount, setTopUpAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankDetails, setBankDetails] = useState({
    accountNumber: '',
    branchCode: '',
    accountHolder: '',
    accountType: 'CHEQUE',
  });

  const [loading, setLoading] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [points, setPoints] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const timerRef = useRef(null);

  // Fetch user points
  const fetchUserPoints = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPoints(response.data.points || 0);
    } catch (error) {
      console.error('Error fetching user points:', error.response?.data || error.message);
      setNotification('Failed to load balance. Please refresh.');
      timerRef.current = setTimeout(() => setNotification(null), 5000);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPoints();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Handle Top-Up
  const handleTopUp = async () => {
    const amount = Number(topUpAmount);
    if (!amount || amount <= 0) {
      setNotification('Please enter a valid top-up amount');
      timerRef.current = setTimeout(() => setNotification(null), 5000);
      return;
    }

    try {
      setLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setNotification('User not authenticated');
        timerRef.current = setTimeout(() => setNotification(null), 5000);
        setLoading(false);
        return;
      }

      const token = await user.getIdToken();

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/wallet/topup`,
        { amount, email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { paymentUrl } = response.data;
      if (!paymentUrl) {
        setNotification('Failed to initialize payment');
        timerRef.current = setTimeout(() => setNotification(null), 5000);
        setLoading(false);
        return;
      }

      window.location.href = paymentUrl; // Redirect to PayFast
    } catch (error) {
      console.error('Top-up error:', error);
      setNotification('Error initializing top-up payment');
      timerRef.current = setTimeout(() => setNotification(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Handle Withdraw
  const handleWithdraw = async () => {
    const pointsRequested = Number(withdrawAmount);
    if (!pointsRequested || pointsRequested <= 0) {
      setNotification('Please enter a valid withdrawal amount');
      timerRef.current = setTimeout(() => setNotification(null), 5000);
      return;
    }

    if (!bankDetails.accountNumber || !bankDetails.branchCode || !bankDetails.accountHolder) {
      setNotification('Please fill in all bank details');
      timerRef.current = setTimeout(() => setNotification(null), 5000);
      return;
    }

    try {
      setWithdrawing(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setNotification('User not authenticated');
        timerRef.current = setTimeout(() => setNotification(null), 5000);
        setWithdrawing(false);
        return;
      }

      const token = await user.getIdToken();

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/wallet/withdraw`,
        {
          pointsRequested,
          accountHolder: bankDetails.accountHolder,
          branchCode: bankDetails.branchCode,
          accountNumber: bankDetails.accountNumber,
          accountType: bankDetails.accountType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.message) {
        setNotification(response.data.message);
        fetchUserPoints();
      } else {
        setNotification(response.data.error || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Withdrawal error:', error.response?.data || error.message);
      setNotification('Error processing withdrawal');
    } finally {
      timerRef.current = setTimeout(() => setNotification(null), 5000);
      setWithdrawing(false);
    }
  };

  return (
    <div className="wallet-container">
      {notification && (
        <div className={`wallet-notification ${notification.toLowerCase().includes('failed') || notification.toLowerCase().includes('error') ? 'error' : 'success'}`}>
          {notification}
        </div>
      )}

      <h2>Wallet</h2>

      <div className="wallet-balance">
        <strong>Current Balance:</strong>{' '}
        {balanceLoading ? 'Loading...' : `${points} points`}
      </div>

      {/* Top-Up Section */}
      <div className="wallet-section">
        <h3>Top Up Points (ZAR only)</h3>
        <div className="input-group">
          <label>Amount (Rands)</label>
          <input
            type="number"
            min="1"
            placeholder="Enter amount"
            value={topUpAmount}
            onChange={(e) => setTopUpAmount(e.target.value)}
            disabled={loading}
          />
        </div>
        <button
          onClick={handleTopUp}
          disabled={loading || !topUpAmount}
          className="wallet-button"
        >
          {loading ? 'Processing...' : 'Top Up'}
        </button>
      </div>

      {/* Withdrawal Section */}
      <div className="wallet-section">
        <h3>Withdraw Points</h3>
        <div className="input-group">
          <label>Amount (Points)</label>
          <input
            type="number"
            min="1"
            placeholder="Enter points to withdraw"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            disabled={withdrawing}
          />
        </div>

        <div className="input-group">
          <label>Account Holder</label>
          <input
            type="text"
            placeholder="Full name"
            value={bankDetails.accountHolder}
            onChange={(e) => setBankDetails({ ...bankDetails, accountHolder: e.target.value })}
            disabled={withdrawing}
          />
        </div>

        <div className="input-group">
          <label>Account Number</label>
          <input
            type="text"
            placeholder="Bank account number"
            value={bankDetails.accountNumber}
            onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
            disabled={withdrawing}
          />
        </div>

        <div className="input-group">
          <label>Branch Code</label>
          <input
            type="text"
            placeholder="Branch code"
            value={bankDetails.branchCode}
            onChange={(e) => setBankDetails({ ...bankDetails, branchCode: e.target.value })}
            disabled={withdrawing}
          />
        </div>

        <div className="input-group">
          <label>Account Type</label>
          <select
            value={bankDetails.accountType}
            onChange={(e) => setBankDetails({ ...bankDetails, accountType: e.target.value })}
            disabled={withdrawing}
          >
            <option value="CHEQUE">Cheque</option>
            <option value="SAVINGS">Savings</option>
          </select>
        </div>

        <button
          onClick={handleWithdraw}
          disabled={withdrawing || !withdrawAmount}
          className="wallet-button withdraw-button"
        >
          {withdrawing ? 'Processing...' : 'Withdraw'}
        </button>
      </div>
    </div>
  );
}

export default Wallet;
